import qs from "query-string";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

type QueryValue = string | number | null | (string | number | null)[];

export const useQueryString = () => {
  const { search } = useLocation();
  const { push, replace } = useHistory();

  const urlQueries = useMemo(
    () =>
      qs.parse(search, {
        arrayFormat: "bracket-separator",
        arrayFormatSeparator: "|"
      }),
    [search]
  );

  const addToQS = useCallback(
    ({
      queryObject,
      pathname,
      isReplace
    }: {
      queryObject: Record<string, QueryValue>;
      pathname?: string;
      isReplace?: boolean;
    }) => {
      const routerMethod = isReplace ? replace : push;

      const currentQuery = qs.parse(window.location.search);

      routerMethod({
        pathname,
        search: qs.stringify(
          {
            ...currentQuery,
            ...queryObject
          },
          {
            arrayFormat: "bracket-separator",
            arrayFormatSeparator: "|",
            skipEmptyString: true,
            skipNull: true
          }
        )
      });
    },
    [push, replace]
  );

  const overrideQS = useCallback(
    ({
      queryObject,
      pathname,
      isReplace
    }: {
      queryObject: Record<string, QueryValue>;
      pathname?: string;
      isReplace?: boolean;
    }) => {
      const routerMethod = isReplace ? replace : push;

      routerMethod({
        pathname,
        search: qs.stringify(
          {
            ...queryObject
          },
          {
            arrayFormat: "bracket-separator",
            arrayFormatSeparator: "|",
            skipEmptyString: true,
            skipNull: true
          }
        )
      });
    },
    [push, replace]
  );

  const removeFromQS = useCallback(
    ({ keys, isReplace }: { keys: string[]; isReplace?: boolean }) => {
      const routerMethod = isReplace ? replace : push;

      const currentQuery = qs.parse(window.location.search);

      const removalObj = keys.reduce(
        (base, key) => ({
          ...base,
          [key]: null
        }),
        {}
      );

      routerMethod({
        search: qs.stringify(
          {
            ...currentQuery,
            ...removalObj
          },
          {
            arrayFormat: "bracket-separator",
            arrayFormatSeparator: "|",
            skipEmptyString: true,
            skipNull: true
          }
        )
      });
    },
    [push, replace]
  );

  const clearQS = useCallback(
    ({ isReplace }: { isReplace?: boolean }) => {
      const routerMethod = isReplace ? replace : push;

      routerMethod({
        search: ""
      });
    },
    [push, replace]
  );

  return {
    urlQueries,
    addToQS,
    overrideQS,
    removeFromQS,
    clearQS
  };
};
